import React, { useState, useEffect } from "react";
import { Form, Button, Card, Row, Col, ListGroup, Badge } from "react-bootstrap";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { getFirestore, collection, getDocs, addDoc, doc, updateDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { toast } from "react-toastify";
import { spinner } from "../utils/Helper";

export default function RoleManager({ theme = 'dark', documentsList = [], refreshDocs }) {
    const isDark = theme === 'dark';
    const db = getFirestore();

    const [profiles, setProfiles] = useState([]);
    const [roles, setRoles] = useState([]);
    
    // Core Form States
    const [roleName, setRoleName] = useState("");
    const [selectedReaders, setSelectedReaders] = useState([]);
    const [selectedEditors, setSelectedEditors] = useState([]);
    const [selectedDeleters, setSelectedDeleters] = useState([]); 
    const [selectedCreators, setSelectedCreators] = useState([]); 
    const [editingRoleId, setEditingRoleId] = useState(null);
    
    // Linked Documents Sub-selection state
    const [linkedDocIds, setLinkedDocIds] = useState([]);

    const cardBg = isDark ? "bg-dark text-white border-secondary border-opacity-50" : "bg-light text-dark border-light-subtle";

    useEffect(() => {
        fetchProfilesAndRoles();
    }, []);

    const fetchProfilesAndRoles = async () => {
        try {
            spinner(true);
            const profilesSnapshot = await getDocs(collection(db, "profiles"));
            setProfiles(profilesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

            const rolesSnapshot = await getDocs(collection(db, "roles"));
            setRoles(rolesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            spinner(false);
        } catch (error) {
            spinner(false);
            toast.error("Database initialization failed.");
        }
    };

    const handleSelectUser = (id, type) => {
        if (type === 'reader') {
            setSelectedReaders(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
        } else if (type === 'editor') {
            setSelectedEditors(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
        } else if (type === 'deleter') {
            setSelectedDeleters(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
        } else {
            setSelectedCreators(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
        }
    };

    const handleSelectDocument = (docId) => {
        setLinkedDocIds(prev => 
            prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
        );
    };

    const handleSubmitRole = async (e) => {
        e.preventDefault();
        if (!roleName.trim()) return toast.warning("Please provide a Role Name");

        const rolePayload = {
            roleName: roleName.trim(),
            allowedReaders: selectedReaders,
            allowedEditors: selectedEditors,
            allowedDeleters: selectedDeleters, 
            allowedCreators: selectedCreators, 
            associatedDocs: linkedDocIds,
            updatedAt: new Date()
        };

        try {
            spinner(true);
            let finalRoleId = editingRoleId;

            if (editingRoleId) {
                await updateDoc(doc(db, "roles", editingRoleId), rolePayload);
            } else {
                const newRoleRef = await addDoc(collection(db, "roles"), rolePayload);
                finalRoleId = newRoleRef.id;
            }

            const batch = writeBatch(db);
            documentsList.forEach((document) => {
                const currentDocRoles = document.associatedRoles || [];
                const shouldHaveRole = linkedDocIds.includes(document.id);
                
                let updatedRolesArr;
                if (shouldHaveRole && !currentDocRoles.includes(finalRoleId)) {
                    updatedRolesArr = [...currentDocRoles, finalRoleId];
                } else if (!shouldHaveRole && currentDocRoles.includes(finalRoleId)) {
                    updatedRolesArr = currentDocRoles.filter(rId => rId !== finalRoleId);
                }

                if (updatedRolesArr) {
                    batch.update(doc(db, "docs", document.id), { associatedRoles: updatedRolesArr });
                }
            });

            await batch.commit();
            if (refreshDocs) refreshDocs();
            
            toast.success("Security group rules committed!");
            clearForm();
            fetchProfilesAndRoles();
        } catch (error) {
            spinner(false);
            console.error(error);
            toast.error("Error committing role configurations.");
        }
    };

    const handleEditInitiate = (role) => {
        setEditingRoleId(role.id);
        setRoleName(role.roleName);
        setSelectedReaders(role.allowedReaders || []);
        setSelectedEditors(role.allowedEditors || []);
        setSelectedDeleters(role.allowedDeleters || []);
        setSelectedCreators(role.allowedCreators || []);
        setLinkedDocIds(role.associatedDocs || []);
    };

    const handleDeleteRole = async (roleId) => {
        if (window.confirm("Delete this security group? This updates linked files.")) {
            try {
                spinner(true);
                await deleteDoc(doc(db, "roles", roleId));
                
                const batch = writeBatch(db);
                documentsList.forEach((document) => {
                    if (document.associatedRoles?.includes(roleId)) {
                        const updated = document.associatedRoles.filter(rId => rId !== roleId);
                        batch.update(doc(db, "docs", document.id), { associatedRoles: updated });
                    }
                });
                await batch.commit();
                if (refreshDocs) refreshDocs();

                toast.success("Role purged successfully.");
                fetchProfilesAndRoles();
            } catch (error) {
                spinner(false);
                toast.error("Failed to cleanly delete role references.");
            }
        }
    };

    const clearForm = () => {
        setEditingRoleId(null);
        setRoleName("");
        setSelectedReaders([]);
        setSelectedEditors([]);
        setSelectedDeleters([]);
        setSelectedCreators([]);
        setLinkedDocIds([]);
    };

    return (
        <div className="w-100 px-1 py-2">
            <Row className="g-3 flex-column">
                <Col width="100%">
                    <Card className={cardBg}>
                        <Card.Header className="fw-bold orange small border-bottom border-secondary border-opacity-25 d-flex justify-content-between align-items-center">
                            <span>{editingRoleId ? "🔧 EDIT MODE: Config Matrix" : "👥 Create New Document Role"}</span>
                            {editingRoleId && <Badge bg="warning" text="dark">Linking Active</Badge>}
                        </Card.Header>
                        <Card.Body className="p-3">
                            <Form onSubmit={handleSubmitRole}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="m-0 small text-muted mb-1" style={{fontSize: '11px'}}>Role Designation Title</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="e.g., Audit Committee, Core Executive"
                                        value={roleName}
                                        onChange={(e) => setRoleName(e.target.value)}
                                        className={isDark ? "glass-input" : ""}
                                        style={{fontSize: '13px'}}
                                    />
                                </Form.Group>

                                <Form.Label className="m-0 small text-muted mb-1" style={{fontSize: '11px'}}>Assign Profile Clearances</Form.Label>
                                <div className=" rounded p-2 mb-3  bg-black custom-scrollbar" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                    <ListGroup variant="flush">
                                        {profiles.map(p => (
                                            <ListGroup.Item key={p.id} className={`d-flex justify-content-between align-items-center p-1 border-0 bg-transparent ${isDark ? 'text-white' : 'text-dark'}`}>
                                                <span className="small text-truncate w-30" style={{fontSize: '12px'}}>{p.name || p.email || p.id}</span>
                                                <div className="d-flex gap-2">
                                                    <Form.Check type="checkbox" id={`r-${p.id}`} label={<span style={{fontSize: '10px'}}>Read</span>} checked={selectedReaders.includes(p.id)} onChange={() => handleSelectUser(p.id, 'reader')} />
                                                    <Form.Check type="checkbox" id={`e-${p.id}`} label={<span style={{fontSize: '10px'}}>Edit</span>} checked={selectedEditors.includes(p.id)} onChange={() => handleSelectUser(p.id, 'editor')} />
                                                    <Form.Check type="checkbox" id={`d-${p.id}`} label={<span style={{fontSize: '10px'}}>Del</span>} checked={selectedDeleters.includes(p.id)} onChange={() => handleSelectUser(p.id, 'deleter')} />
                                                    <Form.Check type="checkbox" id={`c-${p.id}`} label={<span style={{fontSize: '10px'}}>Create</span>} checked={selectedCreators.includes(p.id)} onChange={() => handleSelectUser(p.id, 'creator')} />
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </div>

                                <Form.Label className="m-0 small text-muted mb-1" style={{fontSize: '11px'}}>🔗 Direct Target Document Attachments</Form.Label>
                                <div className="rounded p-2 mb-3 bg-black custom-scrollbar" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                    {documentsList.length === 0 ? (
                                        <div className="text-muted text-center small py-2">No documents available to link.</div>
                                    ) : (
                                        <ListGroup variant="flush">
                                            {documentsList.map(docItem => (
                                                <ListGroup.Item key={docItem.id} className={`d-flex justify-content-between align-items-center p-1 border-0 bg-transparent ${isDark ? 'text-white' : 'text-dark'}`}>
                                                    <span className="small text-truncate w-70" style={{fontSize: '12px'}}>📄 {docItem.name || 'Unnamed Doc'}</span>
                                                    <Form.Check 
                                                        type="checkbox" 
                                                        id={`doclink-${docItem.id}`} 
                                                        label={<span style={{fontSize: '11px'}}>Link</span>}
                                                        checked={linkedDocIds.includes(docItem.id)} 
                                                        onChange={() => handleSelectDocument(docItem.id)} 
                                                    />
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </div>

                                <div className="d-flex gap-2 justify-content-end">
                                    {editingRoleId && <Button variant="secondary" size="sm" style={{fontSize: '12px'}} onClick={clearForm}>Cancel</Button>}
                                    <Button 
                                        type="submit" 
                                        size="sm"
                                        className="fw-bold shadow-sm btn-outline-info btn btn-primary btn btn-primary"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {editingRoleId ? "Save Rule Modifiers" : "Generate Group"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                <Col width="100%">
                    <Card className={cardBg}>
                        <Card.Header className="fw-bold orange small border-bottom border-secondary border-opacity-25">
                            Active Document Roles
                        </Card.Header>
                        <Card.Body className="p-2 custom-scrollbar" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {roles.length === 0 ? (
                                <p className="text-muted small text-center my-3">No configurations built.</p>
                            ) : (
                                <ListGroup variant="flush">
                                    {roles.map(role => (
                                        <ListGroup.Item key={role.id} className={`p-2 border-bottom border-secondary border-opacity-10 bg-transparent ${isDark ? 'text-white' : 'text-dark'}`}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="fw-bold text-info small">{role.roleName}</span>
                                                <div className="d-flex gap-2">
                                                    <button type="button" className="circle-btn text-white" onClick={() => handleEditInitiate(role)} title="Edit Role"><FaEdit size={14} /></button>
                                                    <button type="button" className="circle-btn centerCircle btn-delete" onClick={() => handleDeleteRole(role.id)} title="Delete Role"><FaTrashAlt size={12} /></button>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <Badge bg="secondary" style={{fontSize: '9px'}}>Readers: {role.allowedReaders?.length || 0}</Badge>
                                                <Badge bg="primary" style={{fontSize: '9px'}}>Editors: {role.allowedEditors?.length || 0}</Badge>
                                                <Badge bg="danger" style={{fontSize: '9px'}}>Deleters: {role.allowedDeleters?.length || 0}</Badge>
                                                <Badge bg="warning" text="dark" style={{fontSize: '9px'}}>Creators: {role.allowedCreators?.length || 0}</Badge>
                                                <Badge bg="success" style={{fontSize: '9px'}}>Linked Docs: {role.associatedDocs?.length || 0}</Badge>
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}